import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { 
  FiImage, FiCheck, FiRefreshCw, FiZoomIn, FiDownload, 
  FiClock, FiAlertCircle, FiMessageSquare, FiX
} from 'react-icons/fi';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_REVISIONS = 3;

const StatusBadge = ({ status }) => {
  const configs = {
    pending: { label: 'En attente de validation', color: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
    approved: { label: 'Design approuvé', color: 'bg-green-100 text-green-800 border-green-200' },
    revision_requested: { label: 'Révision demandée', color: 'bg-orange-100 text-orange-800 border-orange-200' },
    in_progress: { label: 'En cours', color: 'bg-blue-100 text-blue-800 border-blue-200' },
  };
  const config = configs[status] || configs.pending;
  return <Badge className={`${config.color} border`}>{config.label}</Badge>;
};

export default function DesignValidation({ reservationId, onStatusChange }) {
  const [loading, setLoading] = useState(true);
  const [design, setDesign] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [remainingRevisions, setRemainingRevisions] = useState(MAX_REVISIONS);
  
  // Modals
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [revisionDialogOpen, setRevisionDialogOpen] = useState(false);
  const [revisionFeedback, setRevisionFeedback] = useState('');
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);

  const loadDesign = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/client-portal/reservations/${reservationId}/design`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.exists) {
          setDesign(data.design);
          setRemainingRevisions(data.remaining_revisions);
        } else {
          setDesign(null);
        }
      }
    } catch (error) {
      console.error('Error loading design:', error);
    } finally {
      setLoading(false);
    }
  }, [reservationId]);

  useEffect(() => {
    loadDesign();
  }, [loadDesign]);

  const getImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    return `${API_URL}${url}`;
  };

  const getCurrentDesign = () => {
    if (!design?.revisions?.length) return null;
    return design.revisions[design.revisions.length - 1];
  };

  const handleApprove = async () => {
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/client-portal/reservations/${reservationId}/design/approve`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        setApproveDialogOpen(false);
        loadDesign();
        if (onStatusChange) onStatusChange('approved');
      } else {
        const error = await response.json();
        alert(error.detail || 'Erreur lors de l\'approbation');
      }
    } catch (error) {
      alert('Erreur de connexion');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRequestRevision = async () => {
    if (!revisionFeedback.trim()) {
      alert('Veuillez décrire les modifications souhaitées');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/client-portal/reservations/${reservationId}/design/request-revision`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ feedback: revisionFeedback })
      });
      
      if (response.ok) {
        const data = await response.json();
        setRevisionDialogOpen(false);
        setRevisionFeedback('');
        setRemainingRevisions(data.remaining_revisions);
        loadDesign();
        if (onStatusChange) onStatusChange('revision_requested');
      } else {
        const error = await response.json();
        alert(error.detail || 'Erreur');
      }
    } catch (error) {
      alert('Erreur de connexion');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Card className="border-slate-200">
        <CardContent className="py-8">
          <div className="flex items-center justify-center">
            <FiRefreshCw className="h-6 w-6 animate-spin text-slate-400" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!design) {
    return (
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <FiImage className="h-5 w-5" />
            Validation du Design
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-6">
            <FiClock className="h-10 w-10 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-600 font-medium">Design en préparation</p>
            <p className="text-sm text-slate-500 mt-1">
              Notre équipe travaille sur votre design personnalisé. Vous serez notifié dès qu'il sera prêt.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const currentDesign = getCurrentDesign();
  const isApproved = design.status === 'approved';
  const canRequestRevision = !isApproved && remainingRevisions > 0;

  return (
    <>
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-base">
              <FiImage className="h-5 w-5" />
              Validation du Design
            </CardTitle>
            <StatusBadge status={design.status} />
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Info Banner */}
          {!isApproved && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <FiAlertCircle className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-blue-700">
                  Vous disposez de <strong>{remainingRevisions} révision(s) gratuite(s)</strong> restantes.
                  Examinez attentivement le design avant de le valider.
                </p>
              </div>
            </div>
          )}

          {/* Current Design Image */}
          {currentDesign && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-slate-700">
                  Version {design.current_version}/{MAX_REVISIONS}
                </span>
                <span className="text-xs text-slate-500">
                  {new Date(currentDesign.created_at).toLocaleDateString('fr-FR')}
                </span>
              </div>
              
              <div 
                className="relative aspect-video bg-slate-100 rounded-lg overflow-hidden cursor-pointer group border border-slate-200"
                onClick={() => {
                  setSelectedImage(getImageUrl(currentDesign.image_url));
                  setPreviewOpen(true);
                }}
              >
                <img
                  src={getImageUrl(currentDesign.image_url)}
                  alt={`Design v${currentDesign.version}`}
                  className="w-full h-full object-contain"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'data:image/svg+xml,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="400" height="300" viewBox="0 0 400 300"><rect fill="#f1f5f9" width="400" height="300"/><text x="200" y="150" text-anchor="middle" fill="#94a3b8" font-family="sans-serif" font-size="14">Image non disponible</text></svg>');
                  }}
                />
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <div className="flex items-center gap-2 text-white">
                    <FiZoomIn className="h-6 w-6" />
                    <span className="text-sm font-medium">Cliquez pour agrandir</span>
                  </div>
                </div>
              </div>

              {currentDesign.notes && (
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-600">{currentDesign.notes}</p>
                </div>
              )}
            </div>
          )}

          {/* Approved Message */}
          {isApproved && (
            <div className="p-4 bg-green-50 rounded-lg text-center border border-green-200">
              <FiCheck className="h-8 w-8 text-green-600 mx-auto mb-2" />
              <p className="font-medium text-green-800">Design validé !</p>
              <p className="text-sm text-green-600 mt-1">
                Validé le {new Date(design.approved_at).toLocaleDateString('fr-FR')}
              </p>
            </div>
          )}

          {/* Action Buttons */}
          {!isApproved && currentDesign && (
            <div className="flex gap-3 pt-2">
              <Button
                onClick={() => setApproveDialogOpen(true)}
                disabled={submitting}
                className="flex-1 bg-green-600 hover:bg-green-700"
                data-testid="approve-design-btn"
              >
                <FiCheck className="mr-2 h-4 w-4" />
                Approuver le design
              </Button>
              <Button
                variant="outline"
                onClick={() => setRevisionDialogOpen(true)}
                disabled={submitting || !canRequestRevision}
                className="flex-1"
                data-testid="request-revision-btn"
              >
                <FiRefreshCw className="mr-2 h-4 w-4" />
                Demander une révision
              </Button>
            </div>
          )}

          {/* Revision History */}
          {design.revision_requests?.length > 0 && (
            <div className="border-t pt-4 mt-4">
              <h4 className="font-medium text-sm mb-3 flex items-center gap-2 text-slate-700">
                <FiMessageSquare className="h-4 w-4" />
                Historique des demandes de révision
              </h4>
              <div className="space-y-2">
                {design.revision_requests.map((req, index) => (
                  <div key={req.id} className="p-3 bg-orange-50 rounded-lg text-sm border border-orange-200">
                    <div className="flex justify-between items-start mb-1">
                      <span className="font-medium text-orange-800">Version {req.version}</span>
                      <span className="text-xs text-orange-600">
                        {new Date(req.requested_at).toLocaleDateString('fr-FR')}
                      </span>
                    </div>
                    <p className="text-orange-700">{req.feedback}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Image Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Aperçu du design</DialogTitle>
          </DialogHeader>
          {selectedImage && (
            <div className="flex justify-center py-4">
              <img
                src={selectedImage}
                alt="Design preview"
                className="max-h-[70vh] object-contain rounded-lg"
              />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewOpen(false)}>
              Fermer
            </Button>
            <Button asChild>
              <a href={selectedImage} download target="_blank" rel="noopener noreferrer">
                <FiDownload className="mr-2 h-4 w-4" />
                Télécharger
              </a>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approve Confirmation Dialog */}
      <Dialog open={approveDialogOpen} onOpenChange={setApproveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmer l'approbation</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <div className="p-4 bg-green-50 rounded-lg border border-green-200 mb-4">
              <FiCheck className="h-8 w-8 text-green-600 mx-auto mb-2" />
              <p className="text-center text-green-800 font-medium">
                Êtes-vous sûr de vouloir approuver ce design ?
              </p>
            </div>
            <p className="text-sm text-slate-600 text-center">
              Une fois approuvé, ce design sera utilisé pour votre événement et 
              vous ne pourrez plus demander de modifications.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveDialogOpen(false)}>
              Annuler
            </Button>
            <Button
              onClick={handleApprove}
              disabled={submitting}
              className="bg-green-600 hover:bg-green-700"
            >
              {submitting ? (
                <FiRefreshCw className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <FiCheck className="mr-2 h-4 w-4" />
              )}
              Confirmer l'approbation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revision Request Dialog */}
      <Dialog open={revisionDialogOpen} onOpenChange={setRevisionDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Demander une révision</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
              <p className="text-sm text-orange-800">
                <strong>Attention :</strong> Il vous reste <strong>{remainingRevisions}</strong> révision(s) gratuite(s).
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Décrivez les modifications souhaitées *
              </label>
              <Textarea
                value={revisionFeedback}
                onChange={(e) => setRevisionFeedback(e.target.value)}
                placeholder="Ex: Pourriez-vous changer la couleur du fond en bleu et agrandir le texte principal..."
                rows={4}
                className="w-full"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevisionDialogOpen(false)}>
              Annuler
            </Button>
            <Button
              onClick={handleRequestRevision}
              disabled={submitting || !revisionFeedback.trim()}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {submitting ? (
                <FiRefreshCw className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <FiRefreshCw className="mr-2 h-4 w-4" />
              )}
              Envoyer la demande
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
